import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Input,
  Table,
  Menu,
  Dropdown,
} from "antd";
import { useHistory } from "react-router-dom";
import {
  getAllCopilotsPaginatedSearch,
  deleteCopilotById,
} from "@api/copilots/browser-extension/copilot/copilot";
import {
  SuccessNotification,
  ErrorNotification,
  CopilotCreateModal,
  CopilotUpdateModal,
} from "@components";
import { IoAddSharp } from "react-icons/io5";

const { Search } = Input;

const CopilotsList = () => {
  const history = useHistory();

  const [copilots, setCopilots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [copilotId, setCopilotId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchTerm(search);
    setPagination({ current: page, pageSize, total: 0 });
    fetchCopilots(page, pageSize, search);
  }, []);

  const fetchCopilots = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    search = searchTerm
  ) => {
    setLoading(true);
    try {
      const res = await getAllCopilotsPaginatedSearch(page, pageSize, search);
      setCopilots(res.data.copilots);
      setPagination((prev) => ({
        ...prev,
        total: res.data.paginationData.totalCopilots,
        current: page,
        pageSize: pageSize,
      }));
    } catch (error) {
      console.error("Fetch copilots error", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchTerm) searchParams.set("search", searchTerm);

    history.push({ search: searchParams.toString() });
    setPagination({ ...pagination, current: page, pageSize });
    fetchCopilots(page, pageSize, searchTerm);
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setPagination({ ...pagination, current: 1 }); // Reset to first page
    fetchCopilots(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Handle create copilot modal
  const handleCreateModalClick = () => {
    setCreateModalOpen(!createModalOpen);
  };

  // Handle update copilot modal
  const handleUpdateModalClick = (id) => {
    setCopilotId(id);
    setUpdateModalOpen(!updateModalOpen);
  };

  // Handle copilot delete
  const handleCopilotDelete = (id) => {
    // Confirm delete
    Modal.confirm({
      title: "Are you sure you want to delete this copilot?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteCopilotById(id);
          // Fetch all copilots
          fetchCopilots();
          // Show success notification
          SuccessNotification("Copilot deleted successfully");
        } catch (error) {
          // Show error notification
          ErrorNotification("Error deleting copilot");
        }
      },
    });
  };

  // Dropdown menu for additional actions
  const menu = (
    <Menu>
      {/* Browser Copilots Categories */}
      <Menu.Item
        key="1"
        onClick={() => {
          history.push("/super-admin/copilots/browser-extension/categories");
        }}
      >
        Browser Copilots Categories
      </Menu.Item>

      {/* Browser Copilot Chats */}
      <Menu.Item
        key="2"
        onClick={() => {
          history.push("/super-admin/copilots/browser-extension/copilot-chats");
        }}
      >
        Browser Copilot Chats
      </Menu.Item>

      {/* Mobile App Copilot Chats Categories */}
      <Menu.Item
        key="4"
        onClick={() => {
          history.push("/super-admin/copilots/mobile-app/categories");
        }}
      >
        Mobile App Copilot Chat Categories
      </Menu.Item>

      {/* Mobile App Copilot Chats */}
      <Menu.Item
        key="3"
        onClick={() => {
          history.push("/super-admin/copilots/mobile-app/copilot-chats");
        }}
      >
        Mobile App Copilot Chats
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      width: 100,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 150,
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 100,
      ellipsis: true,
      render: (text) =>
        text && text.length > 30 ? `${text.substring(0, 30)}...` : text,
    },
    {
      title: "Temperature",
      dataIndex: "temperature",
      key: "temperature",
      width: 150,
    },
    {
      title: "Max Tokens",
      dataIndex: "max_tokens",
      key: "max_tokens",
      width: 150,
    },
    {
      title: "Prompt Inputs",
      dataIndex: "inputs",
      key: "inputs",
      width: 150,
      render: (inputs) => inputs.length,
    },
    {
      title: "Is Useful",
      dataIndex: "useful_generations",
      key: "useful_generations",
      width: 150,
      render: (useful_generations) => useful_generations?.length || 0,
    },
    {
      title: "Is Poor",
      dataIndex: "poor_generations",
      key: "poor_generations",
      width: 150,
      render: (poor_generations) => poor_generations?.length || 0,
    },
    {
      title: "Usage",
      dataIndex: "used",
      key: "used",
      width: 150,
    },
    {
      title: "Likes",
      dataIndex: "likes",
      key: "likes",
      width: 150,
      render: (likes) => likes?.length || 0,
    },
    {
      title: "Bookmarks",
      dataIndex: "bookmarks",
      key: "bookmarks",
      width: 150,
      render: (bookmarks) => bookmarks?.length || 0,
    },
    {
      title: "Action",
      key: "action",
      width: 200,
      render: (_, record) => (
        <>
          <Button
            type="primary"
            size="small"
            style={{ marginRight: "15px" }}
            onClick={() => handleUpdateModalClick(record._id)}
          >
            Edit
          </Button>
          <Button
            type="danger"
            size="small"
            onClick={() => handleCopilotDelete(record._id)}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <>
      <Card style={{ borderRadius: "0", height: "100vh" }}>
        {/* Create Copilot Modal */}
        {createModalOpen && (
          <CopilotCreateModal
            open={createModalOpen}
            handleModalClick={handleCreateModalClick}
            fetchCopilots={fetchCopilots}
          />
        )}

        {/* Update Copilot Modal */}
        {updateModalOpen && (
          <CopilotUpdateModal
            open={updateModalOpen}
            handleModalClick={handleUpdateModalClick}
            fetchCopilots={fetchCopilots}
            copilotId={copilotId}
          />
        )}
        <Row>
          <Col span={24}>
            <Row justify="space-between">
              <Col span={12}>
                <h3 className="hp-mb-16">Browser - Copilots List</h3>
              </Col>

              <Col>
                <Search
                  placeholder="Search by category, copilot name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onSearch={handleSearch}
                  style={{ width: "300px", marginRight: 16 }}
                />

                {/* Create Copilot Button */}
                <Button
                  type="primary"
                  onClick={handleCreateModalClick}
                  style={{ marginRight: 16 }}
                >
                  Create Copilot
                </Button>

                {/* More Options */}
                <Dropdown overlay={menu} placement="bottomRight">
                  <Button
                    icon={<IoAddSharp />}
                    style={{ marginLeft: "10px" }}
                  />
                </Dropdown>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <Table
                  columns={columns}
                  dataSource={copilots}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                  }}
                  loading={loading}
                  onChange={({ current, pageSize }) =>
                    handlePaginationChange(current, pageSize)
                  }
                  rowKey={(record) => record._id}
                  scroll={{ y: "calc(100vh - 270px)", x: "max-content" }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default CopilotsList;
